import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName, getInitials } from '../../shared/utils/user';
import { DesignTokens } from '../../shared/types/tokens';
import { Card } from '../ui/Card';

interface PartnerBreakdownCardProps {
  email: string;
  display_name?: string;
  ownershipPercentage: number;
  downPaymentShare: number;
  monthlyPaymentShare: number;
  totalInterestShare: number;
  totalPaidShare: number;
}

export function PartnerBreakdownCard({
  email,
  display_name,
  ownershipPercentage,
  downPaymentShare,
  monthlyPaymentShare,
  totalInterestShare,
  totalPaidShare,
}: PartnerBreakdownCardProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const displayName = getDisplayName({ email, display_name });
  const initials = getInitials({ email, display_name });

  return (
    <Card style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{initials}</Text>
          </View>
          <View style={styles.headerText}>
            <Text style={styles.email}>{displayName}</Text>
          </View>
        </View>
        <View style={styles.ownershipBadge}>
          <Text style={styles.ownershipText}>{ownershipPercentage}%</Text>
        </View>
      </View>

      <View style={styles.breakdownGrid}>
        <View style={styles.breakdownItem}>
          <Text style={styles.breakdownLabel}>Down Payment</Text>
          <Text style={styles.breakdownValue}>
            ${downPaymentShare.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>

        <View style={[styles.breakdownItem, styles.breakdownItemHighlight]}>
          <Text style={styles.breakdownLabel}>Monthly Payment</Text>
          <Text style={[styles.breakdownValue, styles.breakdownValueHighlight]}>
            ${monthlyPaymentShare.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
          <Text style={styles.breakdownSubtext}>Per month</Text>
        </View>

        <View style={styles.breakdownItem}>
          <Text style={styles.breakdownLabel}>Total Interest</Text>
          <Text style={styles.breakdownValue}>
            ${totalInterestShare.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>

        <View style={styles.breakdownItem}>
          <Text style={styles.breakdownLabel}>Total Paid</Text>
          <Text style={styles.breakdownValue}>
            ${totalPaidShare.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
        </View>
      </View>
    </Card>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    container: {
      width: '100%',
      padding: utils.spacing.md,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: utils.spacing.md,
      paddingBottom: utils.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      gap: utils.spacing.sm,
    },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: tokens.color.primaryContainer,
      justifyContent: 'center',
      alignItems: 'center',
    },
    avatarText: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    headerText: {
      flex: 1,
    },
    email: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.sm * utils.typography.lineHeight.tight,
    },
    ownershipBadge: {
      backgroundColor: tokens.color.primaryContainer,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
    },
    ownershipText: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    breakdownGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    breakdownItem: {
      flex: 1,
      minWidth: 120,
    },
    breakdownLabel: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      letterSpacing: 0.2,
      textTransform: 'uppercase',
    },
    breakdownValue: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.base * utils.typography.lineHeight.tight,
    },
    breakdownValueHighlight: {
      fontSize: utils.typography.sizes.lg,
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.bold,
      lineHeight: utils.typography.sizes.lg * utils.typography.lineHeight.tight,
    },
    breakdownItemHighlight: {
      backgroundColor: addColorOpacity(tokens.color.primaryContainer, 0.3),
      borderRadius: utils.radius.sm,
      padding: utils.spacing.sm,
      borderWidth: 1,
      borderColor: addColorOpacity(tokens.color.primary, 0.2),
    },
    breakdownSubtext: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.regular,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.xs * utils.typography.lineHeight.normal,
    },
  });
}

